import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Droplets, Flame, Fuel, TrendingUp, Zap } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useHousehold } from "@/context/HouseholdContext";
import { useHouseholdDetail } from "@/hooks/use-household-detail";
import { MonthPicker } from "@/components/common/DatePicker";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

export const TYPES = {
  electricity: { label: "Électricité", unit: "kWh", icon: Zap, tone: "butter", color: "#E8C45E" },
  water: { label: "Eau", unit: "L", icon: Droplets, tone: "sky", color: "#7DB7E0" },
  gas: { label: "Gaz", unit: "kWh", icon: Flame, tone: "peach", color: "#F2A27E" },
  fuel: { label: "Carburant", unit: "L", icon: Fuel, tone: "rose", color: "#E79AAE" },
};
const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmt = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(n);

const ReadingForm = ({ onDone }) => {
  const { active } = useHousehold();
  const [v, setV] = useState({ type: "electricity", period: thisMonth(), quantity: "", cost: "" });
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/households/${active.id}/consumption`, { ...v, quantity: Number(v.quantity), cost: v.cost === "" ? null : Number(v.cost) });
      setV({ ...v, quantity: "", cost: "" });
      toast.success("Relevé enregistré");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="card-surface grid gap-2 p-4 sm:grid-cols-[10rem_15rem_1fr_1fr_auto]" data-testid="consumption-form">
      <Select value={v.type} onValueChange={set("type")}>
        <SelectTrigger data-testid="consumption-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(TYPES).map(([k, t]) => <SelectItem key={k} value={k}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
      <MonthPicker value={v.period} onChange={set("period")} testid="consumption-period-input" />
      <Input type="number" min="0" step="0.1" value={v.quantity} onChange={set("quantity")} placeholder={`Quantité (${TYPES[v.type].unit})`} required data-testid="consumption-quantity-input" />
      <Input type="number" min="0" step="0.01" value={v.cost} onChange={set("cost")} placeholder="Coût € (facultatif)" data-testid="consumption-cost-input" />
      <Button type="submit" className="rounded-full" data-testid="consumption-submit-button">Enregistrer</Button>
    </form>
  );
};

const TypeCard = ({ k, rows }) => {
  const t = TYPES[k];
  const { money } = useFormat();
  const navigate = useNavigate();
  const last = rows[rows.length - 1];
  const prev = rows[rows.length - 2];
  const delta = last && prev && prev.quantity ? Math.round(((last.quantity - prev.quantity) / prev.quantity) * 100) : null;
  const data = rows.slice(-6).map((r) => ({ ...r, label: new Date(`${r.period}-01`).toLocaleDateString("fr-FR", { month: "short" }) }));
  return (
    <div role="button" tabIndex={0} onClick={() => navigate(`/foyer/consommation/${k}`)} onKeyDown={(e) => { if (e.key === "Enter") { navigate(`/foyer/consommation/${k}`); } }} className={cn("card-surface tone-wash cursor-pointer p-5 transition-transform hover:-translate-y-0.5", `tone-${t.tone}`)} data-testid={`consumption-card-${k}`}>
      <div className="flex items-center gap-2"><span className="tone-chip flex h-8 w-8 items-center justify-center rounded-lg"><t.icon className="h-4 w-4" /></span><span className="text-sm font-semibold">{t.label}</span></div>
      {last ? (
        <>
          <div className="mt-3 flex items-baseline gap-2"><span className="num text-2xl font-bold">{fmt(last.quantity)}</span><span className="text-sm text-muted-foreground">{t.unit}/mois</span></div>
          <div className="text-xs text-muted-foreground">{new Date(`${last.period}-01`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}{last.cost != null && ` · ${money(last.cost, { maximumFractionDigits: 2 })}`}{delta != null && <span className={delta > 0 ? " text-destructive" : " text-success"}> · {delta > 0 ? "+" : ""}{delta} %</span>}</div>
          <div className="mt-3 h-24"><ResponsiveContainer width="100%" height="100%"><BarChart data={data}><XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} /><Tooltip formatter={(v) => [`${fmt(v)} ${t.unit}`, "Consommation"]} labelFormatter={(l, p) => p?.[0] ? new Date(`${p[0].payload.period}-01`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : l} cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={{ borderRadius: 12, border: "none", fontSize: 12 }} /><Bar dataKey="quantity" fill={t.color} radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer></div>
        </>
      ) : <p className="mt-3 text-sm text-muted-foreground">Aucun relevé.</p>}
    </div>
  );
};

const ConsumptionAlerts = ({ alerts }) => {
  if (!alerts.length) return null;
  return (
    <ul className="space-y-2" data-testid="consumption-alerts">
      {alerts.map((a) => {
        const t = TYPES[a.type];
        return (
          <li key={a.type} className={cn("card-surface tone-wash flex items-center gap-3 p-4", a.level === "danger" ? "tone-rose" : "tone-butter")} data-testid={`consumption-alert-${a.type}`}>
            <span className="tone-chip flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"><AlertTriangle className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-semibold">Hausse de consommation — {t.label}</div>
              <div className="text-muted-foreground">{a.label}</div>
            </div>
            <span className="num flex shrink-0 items-center gap-1 rounded-full bg-card px-3 py-1 text-sm font-bold text-destructive" data-testid={`consumption-alert-delta-${a.type}`}><TrendingUp className="h-3.5 w-3.5" />+{a.delta} %</span>
          </li>
        );
      })}
    </ul>
  );
};

const THRESHOLDS = [5, 10, 15, 20, 25, 30, 40, 50];

const ThresholdControl = ({ onDone }) => {
  const { data: h, canManage } = useHouseholdDetail();
  if (!h) return null;
  const save = async (val) => {
    try {
      await api.patch(`/households/${h.id}`, { alert_threshold: Number(val) });
      toast.success(`Seuil d'alerte réglé à ${val} %`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="card-surface flex flex-wrap items-center gap-3 p-4 text-sm" data-testid="consumption-threshold">
      <span className="min-w-0 flex-1">
        <span className="font-semibold">Seuil d'alerte du foyer</span>
        <span className="block text-xs text-muted-foreground">Alerte quand l'électricité ou l'eau dépasse le mois précédent de ce pourcentage (alerte forte au double).</span>
      </span>
      {canManage ? (
        <Select value={String(h.alert_threshold)} onValueChange={save}>
          <SelectTrigger className="w-28 rounded-full" data-testid="consumption-threshold-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[...new Set([...THRESHOLDS, h.alert_threshold])].sort((a, b) => a - b).map((t) => <SelectItem key={t} value={String(t)}>+{t} %</SelectItem>)}</SelectContent>
        </Select>
      ) : <span className="num rounded-full bg-secondary px-3 py-1 font-bold" data-testid="consumption-threshold-value">+{h.alert_threshold} %</span>}
    </div>
  );
};

export const ConsumptionPanel = () => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["consumption", active.id], queryFn: () => api.get(`/households/${active.id}/consumption`).then((r) => r.data) });
  const alerts = useQuery({ queryKey: ["consumption-alerts", active.id], queryFn: () => api.get(`/households/${active.id}/consumption/alerts`).then((r) => r.data) });
  const refresh = () => ["consumption", "consumption-alerts", "dashboard", "household"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  return (
    <section className="space-y-4" data-testid="consumption-panel">
      <ConsumptionAlerts alerts={alerts.data || []} />
      <ThresholdControl onDone={refresh} />
      <ReadingForm onDone={refresh} />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Object.keys(TYPES).map((k) => <TypeCard key={k} k={k} rows={data.filter((r) => r.type === k)} />)}
      </div>
      <p className="text-xs text-muted-foreground">Saisie manuelle des relevés mensuels. Les alertes s'affichent pour tout le foyer, ici et sur l'accueil. La lecture automatique des factures d'énergie et d'eau arrivera avec CASHMY Docs (Partie 3).</p>
    </section>
  );
};
